import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { staffProfileAPI } from 'api/temple'
import { apiStatus } from 'shared/constants/AppEnum'
import { AppState } from 'store/reduxStore'
import { IApiState } from 'store/types'
import { StaffProfilePayloadType, StaffProfileResponseType, TempleIdType } from 'types/api/temple'
import { selectCurrentTempleId } from './templeSlice'

const staffProfileAdapter = createEntityAdapter<StaffProfileResponseType>({
    sortComparer: (a, b) => a.fullName.localeCompare(b.fullName),
})

type InitialStateType = {
    api: IApiState
}

const initialState: InitialStateType = {
    api: {
        status: apiStatus.IDLE,
        error: null,
    },
}

export const staffProfileListThunk = createAsyncThunk(
    'staffProfile/list',
    async (payload: TempleIdType = {}, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffProfileAPI.list(templeId)
            return response.data.map(profile => ({ ...profile, templeId }))
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffProfileRetrieveThunk = createAsyncThunk(
    'staffProfile/retrieve',
    async (payload: IdType & TempleIdType, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffProfileAPI.get(payload.id, templeId)
            return { ...response.data, templeId }
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffProfileCreateThunk = createAsyncThunk(
    'staffProfile/create',
    async (payload: StaffProfilePayloadType & TempleIdType, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffProfileAPI.post(payload, templeId)
            return { ...response.data, templeId }
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffProfileUpdateThunk = createAsyncThunk(
    'staffProfile/update',
    async (payload: IdType & TempleIdType & { data: Partial<StaffProfilePayloadType> }, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffProfileAPI.update(payload.id, payload.data, templeId)
            return { ...response.data, templeId }
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffProfileDeleteThunk = createAsyncThunk(
    'staffProfile/delete',
    async (payload: IdType & TempleIdType, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            await staffProfileAPI.delete(payload.id, templeId)
            return payload.id
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

const staffProfileSlice = createSlice({
    name: 'staffProfile',
    initialState: staffProfileAdapter.getInitialState(initialState),
    reducers: {
        resetState(state) {
            state.api = {
                status: apiStatus.IDLE,
                error: null,
            }
            staffProfileAdapter.removeAll(state)
        },
    },
    extraReducers: builder => {
        const pending = (state: any) => {
            state.api = {
                status: apiStatus.LOADING,
                error: null,
            }
        }
        const rejected = (state: any, action: any) => {
            state.api = {
                status: apiStatus.FAILED,
                error: action.payload,
            }
        }

        builder.addCase(staffProfileListThunk.pending, pending)
        builder.addCase(staffProfileListThunk.fulfilled, (state, action) => {
            state.api = {
                status: apiStatus.SUCCEEDED,
                error: null,
            }
            staffProfileAdapter.upsertMany(state, action.payload)
        })
        builder.addCase(staffProfileListThunk.rejected, rejected)
        builder.addCase(staffProfileRetrieveThunk.pending, pending)
        builder.addCase(staffProfileRetrieveThunk.fulfilled, (state, action) => {
            state.api = {
                status: apiStatus.SUCCEEDED,
                error: null,
            }
            staffProfileAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffProfileRetrieveThunk.rejected, rejected)
        builder.addCase(staffProfileCreateThunk.pending, pending)
        builder.addCase(staffProfileCreateThunk.fulfilled, (state, action) => {
            state.api = {
                status: apiStatus.SUCCEEDED,
                error: null,
            }
            staffProfileAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffProfileCreateThunk.rejected, rejected)
        builder.addCase(staffProfileUpdateThunk.pending, pending)
        builder.addCase(staffProfileUpdateThunk.fulfilled, (state, action) => {
            state.api = {
                status: apiStatus.SUCCEEDED,
                error: null,
            }
            staffProfileAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffProfileUpdateThunk.rejected, rejected)
        builder.addCase(staffProfileDeleteThunk.pending, pending)
        builder.addCase(staffProfileDeleteThunk.fulfilled, (state, action) => {
            state.api = {
                status: apiStatus.SUCCEEDED,
                error: null,
            }
            staffProfileAdapter.removeOne(state, action.payload)
        })
        builder.addCase(staffProfileDeleteThunk.rejected, rejected)
    },
})

export default staffProfileSlice.reducer

export const { resetState } = staffProfileSlice.actions

export const {
    selectAll: selectAllStaffProfiles,
    selectById: selectStaffProfileById,
} = staffProfileAdapter.getSelectors((state: AppState) => state.staffProfile)

export const selectStaffProfileStatus = (state: AppState): IApiState => state.staffProfile.api

export const selectCurrentTempleStaffProfiles = createSelector(
    [selectAllStaffProfiles, selectCurrentTempleId],
    (profiles, currentTemple) => profiles.filter(profile => profile.templeId === currentTemple)
)
